import RequestToken from '../../../../tokens/RequestToken';
import {CommandRequester} from '../../../../CommandRequester';
import RequestAPI from '../../../RequestAPI';
import {AsyncCommandEvent, CommandTypes, LookAtEvents} from '@jibo/command-protocol';


/**
 * Target options for Look
 * @typedef CommandRequester.expression.TargetMode
 * @prop Position
 * @prop Angle
 * @prop Entity
 * @prop Scren
 * @intdocs
 */
export enum TargetMode {
    Position,
    Angle,
    Entity,
    ScreenCoords
}


export interface LookAtAchievedResult {
    AngleTarget: JIBO.v1.AngleVector;
    PositionTarget: JIBO.v1.Vector3;
}

export interface LookAtTrackLostResult {
    EntityTarget: JIBO.v1.LookAtEntity;
    AngleTarget: JIBO.v1.AngleVector;
    PositionTarget: JIBO.v1.Vector3;
}


/**
 * @class LookToken
 * @extends RequestToken
 * @hideconstructor
 */
export class LookToken extends RequestToken<JIBO.v1.LookAtRequest, LookAtAchievedResult|LookAtTrackLostResult> {

    /** @private */
    constructor(owner: CommandRequester, protocol: JIBO.v1.LookAtRequest) {
        super(owner, protocol);
    }

    /**
     * @private
     */
    public handleAck(ack:JIBO.v1.Acknowledgement) {
        //handle ack
        if (ack.Response.ResponseCode >= 400) {
            this.isComplete = true;
            this._complete.reject(ack.Response);
            return;
        }
    }

    /**
     * @private
     */
    public handleEvent(event:JIBO.v1.EventMessage) {
        //handle event
        const eventData = event.EventBody;
        switch (eventData.Event) {
            case AsyncCommandEvent.Stop:
                this.isComplete = true;
                this._complete.resolve();
                break;
            case AsyncCommandEvent.Error:
                this.isComplete = true;
                this._complete.reject(eventData);
                break;
            case LookAtEvents.LookAtAchieved:
                this.isComplete = true;
                this._complete.resolve(eventData);
                break;
            case LookAtEvents.TrackEntityLost:
                this.isComplete = true;
                this._complete.resolve(eventData);
                break;
        }
    }
}

/**
 * Internal Look class
 * @class CommandRequester.expression.Look
 * @intdocs
 */
export class Look extends RequestAPI<JIBO.v1.LookAtRequest, LookToken> {

    /**
     * Generate Look Protocol
     * @method CommandRequester.expression.Look#generateProtocol
     * @param {CommandRequester.expression.TargetMode} targetMode - How we are targetting our target (depends on the target type)
     * @param {(CommandRequester.Vector2|CommandRequester.Vector3|CommandRequester.expression.LookAtEntity)} target - What we're looking at (Entity, Angle or Point in space)
     * @param {boolean} levelHead - `true` to keep Jibo's head level while he moves.
     * @param {boolean} [shouldTrack=false] - If we should track the target entity (Entity-Mode only)
     * @returns {LookAtRequest}
     * @intdocs
     */
    static generateProtocol(
        targetMode: TargetMode,
        target: JIBO.v1.Vector2 | JIBO.v1.Vector3 | JIBO.v1.LookAtEntity | JIBO.v1.AngleVector,
        levelHead: boolean,
        shouldTrack = false): JIBO.v1.LookAtRequest {
        let lookAtTarget: any; //JIBO.v1.LookAtTarget;
        switch (targetMode) {
            case TargetMode.Position:
                lookAtTarget = { Position: target as JIBO.v1.Vector3 };
                break;
            case TargetMode.Angle:
                lookAtTarget = { Angle: target as JIBO.v1.AngleVector };
                break;
            case TargetMode.Entity:
                lookAtTarget = { Entity: target as JIBO.v1.LookAtEntity };
                break;
            case TargetMode.ScreenCoords:
                lookAtTarget = { ScreenCoords: target as JIBO.v1.Vector2 };
                break;
        }
        return {
            Type: CommandTypes.LookAt,
            LookAtTarget: lookAtTarget,
            TrackFlag: shouldTrack,
            LevelHeadFlag: levelHead
        };
    }

    /**
     * Create LookToken from LookAtRequest protocol.
     * @method CommandRequester.expression.Look#generateToken
     * @param {LookAtRequest} protocol - LookAtRequest protocol to generate a LookToken from.
     * @param {boolean} [andSend=false] - `true` if the generated LookToken should also be sent as a request.
     * @return {LookToken}
     * @intdocs
     */
    public generateToken(protocol: JIBO.v1.LookAtRequest, andSend=false): LookToken {
        const token = new LookToken(this.owner, protocol);

        if (andSend) {
            super.sendToken(token);
        }
        return token;
    }

    /**
     * Send LookToken request.
     * <p>NOTE: Implementation in base class {@link RequestAPI}</p>
     * @method CommandRequester.expression.Look#sendToken
     * @param {LookToken} token - LookToken to send.
     * @intdocs
     */
}
